"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { adminAPI, handleApiError } from "../../../services/api";
import "./InvoiceProfileManager.css";

const STATUS_FILTERS = ["pending", "submitted", "accepted", "rejected", "failed"];

const STATUS_BADGE_CLASS = {
  accepted: "ready",
  rejected: "missing",
  failed: "missing",
  pending: "soon",
  submitted: "soon",
};

/**
 * Admin view of per-invoice e-invoicing reporting: what was submitted to
 * the authorities, what happened, and why — plus a manual resubmit for
 * failed/rejected invoices (same invoice number, per the gapless rule).
 */
const EInvoiceReportingManager = () => {
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [resubmittingId, setResubmittingId] = useState(null);
  const requestSeq = useRef(0);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(handler);
  }, [search]);

  const load = useCallback(async (page = 1) => {
    const seq = ++requestSeq.current;
    setIsLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getEInvoiceReports({
        page,
        per_page: 10,
        status: statusFilter || undefined,
        search: debouncedSearch || undefined,
      });
      if (seq !== requestSeq.current) return;
      const data = response?.data?.data || {};
      setItems(data.items || []);
      setPagination(data.pagination || null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(handleApiError(err));
    } finally {
      if (seq === requestSeq.current) setIsLoading(false);
    }
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    load(currentPage);
  }, [load, currentPage]);

  const handleResubmit = async (item) => {
    setResubmittingId(item.id);
    setActionError(null);
    try {
      await adminAPI.resubmitEInvoiceReport(item.id);
      await load(currentPage);
    } catch (err) {
      setActionError(handleApiError(err));
    } finally {
      setResubmittingId(null);
    }
  };

  const tr = (key, fallback) => t(key, fallback);

  return (
    <div className="invoice-manager">
      <div className="invoice-manager-header">
        <p className="invoice-manager-subtitle" style={{ margin: 0 }}>
          {tr(
            "einvoiceReporting.subtitle",
            "Invoices submitted to e-invoicing authorities and their outcomes."
          )}
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <select
            className="invoice-manager-input"
            style={{ minWidth: 150 }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">{tr("einvoiceReporting.allStatuses", "All statuses")}</option>
            {STATUS_FILTERS.map((s) => (
              <option key={s} value={s}>
                {tr(`einvoiceReporting.status.${s}`, s)}
              </option>
            ))}
          </select>
          <input
            type="text"
            className="invoice-manager-search"
            placeholder={tr("einvoiceReporting.searchPlaceholder", "Search invoice or clinic...")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
      </div>

      {error && <div className="invoice-manager-error">{error}</div>}
      {actionError && <div className="invoice-manager-error">{actionError}</div>}

      <div className="invoice-manager-table-wrap">
        <table className="invoice-manager-table einvoice-reporting-table">
          <thead>
            <tr>
              <th>{tr("einvoiceReporting.table.invoice", "Invoice")}</th>
              <th>{tr("einvoiceReporting.table.clinic", "Clinic")}</th>
              <th>{tr("einvoiceReporting.table.client", "Client")}</th>
              <th>{tr("einvoiceReporting.table.total", "Total")}</th>
              <th>{tr("einvoiceReporting.table.status", "Status")}</th>
              <th>{tr("einvoiceReporting.table.reason", "Reason")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="invoice-manager-loading">
                  {tr("common.loading", "Loading...")}
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={7} className="invoice-manager-loading">
                  {tr(
                    "einvoiceReporting.table.empty",
                    "No invoices have been submitted for e-invoicing reporting yet."
                  )}
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id} className="invoice-manager-row">
                  <td className="invoice-manager-clinic-name">{item.invoice_number}</td>
                  <td>{item.clinic_name || "—"}</td>
                  <td>{item.client_name || "—"}</td>
                  <td>
                    {Number(item.total).toFixed(2)} {item.currency}
                  </td>
                  <td>
                    <span className={`invoice-manager-badge ${STATUS_BADGE_CLASS[item.reporting_status] || "soon"}`}>
                      {tr(`einvoiceReporting.status.${item.reporting_status}`, item.reporting_status)}
                    </span>
                  </td>
                  <td style={{ maxWidth: 320, fontSize: 12, color: "#6B7280" }}>
                    {item.reason || "—"}
                  </td>
                  <td className="invoice-manager-actions">
                    {item.can_resubmit && (
                      <button
                        type="button"
                        className="invoice-manager-save"
                        disabled={resubmittingId === item.id}
                        onClick={() => handleResubmit(item)}
                      >
                        {resubmittingId === item.id
                          ? tr("common.loading", "Loading...")
                          : tr("einvoiceReporting.resubmit", "Resubmit")}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pagination && pagination.last_page > 1 && (
        <div className="invoice-manager-pagination">
          <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}>
            ←
          </button>
          <span>
            {pagination.current_page} / {pagination.last_page}
          </span>
          <button
            type="button"
            disabled={currentPage >= pagination.last_page}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            →
          </button>
        </div>
      )}
    </div>
  );
};

export default EInvoiceReportingManager;
